<template>
  <StudentDashboardPageLayout id="tour-student-home-page">
      <div class="grid grid-cols-1 gap-4">
        <StudentDataRequestsCard />
        <StudentProfileReminder />

        <div id="tour-student-home-cards" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <!-- Attendance widget (real data) -->
          <StudentAttendanceWidget />

          <!-- Subjects / Courses card -->
          <div class="bg-white rounded-xl shadow-sm p-4">
            <div class="flex flex-col lg:flex-row lg:items-center gap-4">
              <div class="flex items-center gap-4">
                <div class="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <i class="pi pi-book text-2xl text-primary"></i>
                </div>
                <div>
                  <div class="text-sm text-gray-500 uppercase">{{ labels.SUBJECT }}s</div>

                  <!-- Loading -->
                  <div v-if="loadingSubjects" class="mt-2">
                    <i class="pi pi-spin pi-spinner text-lg text-primary"></i>
                  </div>

                  <!-- Tertiary: show curriculum courses & units -->
                  <template v-else-if="isTertiary">
                    <div class="text-xl font-semibold mt-2">{{ curriculumCourses.length }}</div>
                    <div class="text-xs text-primary mt-2">
                      {{ totalUnits }} unit{{ totalUnits !== 1 ? 's' : '' }} registered
                    </div>
                  </template>

                  <!-- Secondary / Primary: show subjects -->
                  <template v-else>
                    <div class="text-xl font-semibold mt-2">{{ subjects.length }}</div>
                    <div class="text-xs text-primary mt-2">
                      {{ subjects.length ? subjects.slice(0, 3).map(s => s.name).join(' · ') : 'No subjects found' }}
                      <span v-if="subjects.length > 3"> ...</span>
                    </div>
                  </template>
                </div>
              </div>

              <div class="flex items-center justify-between lg:ml-auto lg:flex-col lg:items-end gap-2">
                <div class="text-xs text-gray-500 lg:order-2">{{ isTertiary ? 'View courses' : 'View results' }}</div>
                <Button
                  :label="isTertiary ? 'Courses' : 'Results'"
                  :icon="isTertiary ? 'pi pi-list' : 'pi pi-chart-bar'"
                  class="p-button-sm p-button-outlined"
                  @click="navigateToSubjects"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Upcoming CBT widget -->
        <StudentUpcomingCBTWidget />

        <!-- Timetable / calendar card -->
        <div class="bg-white rounded-xl shadow-sm p-4">
          <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
              <div class="text-sm text-gray-500">Timetable</div>
              <Button
                icon="pi pi-calendar"
                class="p-button-text"
                aria-label="Open timetable"
                v-tooltip.left="'Open timetable'"
                @click="navigateToTimetable"
                data-testid="btn-nav-timetable"
              />
            </div>

            <!-- Loading State -->
            <div v-if="loadingTimetable" class="flex justify-center py-4">
              <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
            </div>

            <!-- No Timetable Found -->
            <div v-else-if="!timetable" class="text-center py-4 text-gray-500 text-sm">
              No timetable found.
            </div>

            <!-- Timetable Grid -->
            <div v-else class="overflow-x-auto">
              <TimetableGrid :slots="sortedSlots" :entries="timetable.entries" :days="days" :editable="false" />
            </div>
          </div>
        </div>
      </div>
  </StudentDashboardPageLayout>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useGetTimetablesQuery } from '@/types/vue-apollo-helpers';

import TimetableGrid from '@/components/DashBoard/Timetable/TimetableGrid.vue';
import StudentAttendanceWidget from './StudentAttendanceWidget.vue';
import StudentUpcomingCBTWidget from './StudentUpcomingCBTWidget.vue';
import StudentProfileReminder from './StudentProfileReminder.vue';
import StudentDataRequestsCard from './StudentDataRequestsCard.vue';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { GET_STUDENT_SUBJECTS } from '@/graphql';
import { TimetableTimeableType } from '@/types/composition-functions';

const { labels } = useSchoolLabels();
const { studentProfile, refetchUser, currentClassroomSection, curriculum } = useUserQuery();
const router = useRouter();

// School type detection
const { isTertiary } = useSchoolFeatures();

onMounted(() => {
  refetchUser();
});

const navigateToTimetable = () => {
  router.push({ name: 'student-timetable' });
};

const navigateToSubjects = () => {
  router.push({ name: isTertiary.value ? 'student-courses' : 'student-result' });
};

// Subjects / Courses logic
// Tertiary: use curriculum data already loaded via useUserQuery
const curriculumCourses = computed(() => curriculum.value || []);
const totalUnits = computed(() =>
  curriculumCourses.value.reduce((sum: number, c) => sum + (c.course?.unit || 0), 0),
);

// Secondary/Primary: fetch subjects via query
const { result: subjectsResult, loading: loadingSubjectsQuery } = useQuery(GET_STUDENT_SUBJECTS,
  () => ({
    student_id: studentProfile.value?.id,
  }),
  {
    enabled: computed(() => !isTertiary.value && !!studentProfile.value?.id),
  },
);

const subjects = computed(() => subjectsResult.value?.studentSubjects || []);
const loadingSubjects = computed(() => isTertiary.value ? false : loadingSubjectsQuery.value);

// Timetable logic
const { result: timetableResult, loading: loadingTimetable } = useGetTimetablesQuery(
  () => ({
    timeable_id: currentClassroomSection.value?.id,
    timeable_type: TimetableTimeableType.ClassroomSection,
  }),
  {
    enabled: computed(() => !!currentClassroomSection.value?.id),
  },
);

const timetable = computed(() => timetableResult.value?.timetables?.data?.[0] || null);

const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const sortedSlots = computed(() => {
  if (!timetable.value?.template?.timeSlots) return [];
  return [...timetable.value.template.timeSlots].sort((a, b) => a.start_time.localeCompare(b.start_time));
});
</script>
